import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getRoles = createAsyncThunk(
  "roles/getRoles",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().roles;
      if (state.data.length <= 0 && state.count.getRoles <= 1) {
        const res = await axios.get("/roles", { params: reqData });
        return res.data;
      }
      return { count: state.count, data: state.data };
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
export const getRole = createAsyncThunk(
  "roles/getRole",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().roles;
      if (state.data.length >= 1) {
        const res = state.data.find((item) => item.id === reqData);
        if (res) {
          return res;
        }
      } else if (state.count.getRole <= 1) {
        const res = await axios.get("/role/" + reqData);
        return res.data;
      } else return state.selected;
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getRolePermissions = createAsyncThunk(
  "roles/getRolePermissions",
  async (id, { rejectWithValue, getState }) => {
    const state = getState().roles;
    try {
      if (state.count.getRolePermissions <= 1) {
        const res = await axios.get(`/role-permissions/${id}`);
        return res.data;
      }
      return state.permissions;
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const editRolePermissions = createAsyncThunk(
  "roles/editRolePermissions",
  async ({ id, data }, { rejectWithValue, getState }) => {
    const state = getState().roles;
    try {
      if (state.count.editRolePermissions <= 1) {
        const res = await axios.put(`/edit-role-permissions/${id}`, data);
        return res.data;
      }
      return state.permissions;
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const addRole = createAsyncThunk(
  "roles/addRole",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post(`/add-role`, reqData);
      return res.data;
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const editRole = createAsyncThunk(
  "roles/editRole",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/edit-role/${id}`, reqData);
      return res.data;
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRole = createAsyncThunk(
  "roles/deleteRole",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`/delete-role/${id}`, {
        params: reqData,
      });
      return res.data;
    } catch (error) {
      if (error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

const rolesSlice = createSlice({
  name: "roles",
  initialState: {
    total: 0,
    data: [],
    permissions: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getRole: false,
      getRoles: false,
      addRole: false,
      editRole: false,
      deleteRole: false,
      getProjectStatus: false,
      getRolePermissions: false,
      editRolePermissions: false,
    },
    count: {
      getRole: 0,
      getRoles: 0,
      addRole: 0,
      editRole: 0,
      deleteRole: 0,

      getProjectStatus: 0,
      getRolePermissions: 0,
      editRolePermissions: 0,
    },
  },
  reducers: {
    setRoles: (state, action) => {
      state.data = action.payload;
    },
    setSelectedRole: (state, action) => {
      state.selected = action.payload;
    },
    unsetSelectedRole: (state) => {
      state.selected = {};
    },
    unsetRoles: (state) => {
      state.data = [];
      state.selected = {};
    },
    unsetRolePermissions: (state) => {
      state.permissions = [];
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Roles
      .addCase(getRoles.pending, (state) => {
        state.load.getRoles = loadStatus.loading;
        state.error = null;
        state.count.getRoles = ++state.count.getRoles;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.load.getRoles = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getRoles = 0;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.load.getRoles = loadStatus.error;
        state.error = action.payload;
        state.count.getRoles = 0;
      })
      //Get Role
      .addCase(getRole.pending, (state) => {
        state.load.getRole = loadStatus.loading;
        state.error = null;
        state.count.getRole = ++state.count.getRole;
      })
      .addCase(getRole.fulfilled, (state, action) => {
        state.load.getRole = loadStatus.success;
        state.selected = action.payload;
        state.count.getRole = 0;
      })
      .addCase(getRole.rejected, (state, action) => {
        state.load.getRole = loadStatus.error;
        state.error = action.payload;
        state.count.getRole = 0;
      })
      //Add Role
      .addCase(addRole.pending, (state) => {
        state.load.addRole = loadStatus.loading;
        state.error = null;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.load.addRole = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addRole.rejected, (state, action) => {
        state.load.addRole = loadStatus.error;
        state.error = action.payload;
      })
      //Update Role
      .addCase(editRole.pending, (state) => {
        state.load.editRole = loadStatus.loading;
        state.error = null;
      })
      .addCase(editRole.fulfilled, (state, action) => {
        state.load.editRole = loadStatus.success;
        const index = state.data.findIndex(
          (role) => (role.id = action.payload.id)
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editRole.rejected, (state, action) => {
        state.load.editRole = loadStatus.error;
        state.error = action.payload;
      })
      //Delete Role
      .addCase(deleteRole.pending, (state) => {
        state.load.deleteRole = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.load.deleteRole = loadStatus.success;
        state.data.filter((role) => role.id !== action.meta.arg.id);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.load.deleteRole = loadStatus.error;
        state.error = action.payload;
      })
      //Get Role Permissions
      .addCase(getRolePermissions.pending, (state) => {
        state.load.getRolePermissions = loadStatus.loading;
        state.error = null;
      })
      .addCase(getRolePermissions.fulfilled, (state, action) => {
        state.load.getRolePermissions = loadStatus.success;
        state.permissions = action.payload;
        state.count.getRolePermissions = 0;
      })
      .addCase(getRolePermissions.rejected, (state, action) => {
        state.load.getRolePermissions = loadStatus.error;
        state.error = action.payload;
        state.count.getRolePermissions = 0;
      })
      //Get Role Permissions
      .addCase(editRolePermissions.pending, (state) => {
        state.load.editRolePermissions = loadStatus.loading;
        state.error = null;
      })
      .addCase(editRolePermissions.fulfilled, (state, action) => {
        state.load.editRolePermissions = loadStatus.success;
        state.permissions = action.payload;
        state.count.editRolePermissions = 0;
      })
      .addCase(editRolePermissions.rejected, (state, action) => {
        state.load.editRolePermissions = loadStatus.error;
        state.error = action.payload;
        state.count.editRolePermissions = 0;
      });
  },
});

export const {
  setRoles,
  setSelectedRole,
  unsetRoles,
  unsetRolePermissions,
  unsetSelectedRole,
} = rolesSlice.actions;
export default rolesSlice.reducer;
